"use client";
import Link from "next/link";
import { GetDefaultImagePath } from "../defaultImage/DefaultImage";
import { LogoPath } from "../defaultImage/DefaultPaths";
import { SanityImage } from "@/sanity/SanityImage";
import CheckLink from "../../../utils/constants/LinkCheck";
import { usePathname } from "next/navigation";
import RouteOneFinancingButton from "@/components/finance/RouteOneFinancingButton";

interface FooterProps {
    headerData?: Record<string, any>;
    footerData?: Record<string, any>;
    facets?: {
        make?: Array<{
            value: string;
            count?: number;
        }>;
    };
}

const Footer = (props: FooterProps) => {
    const pathname = usePathname();
    const isReferralPath = pathname.includes("/referral");

    const facebookLink = props.footerData?.["facebookLink"]?.link?.trim();
    const instagramLink = props.footerData?.["instagramLink"]?.link?.trim();
    const hasFacebookLink = Boolean(facebookLink && facebookLink !== "#");
    const hasInstagramLink = Boolean(instagramLink && instagramLink !== "#");

    // Get current year for copyright
    const currentYear = new Date().getFullYear();

    return (
        <footer
            className="t-bg-automotive-dark t-text-white t-py-12 lg:t-py-16 print:t-hidden"
            role="contentinfo">
            <div className="t-max-w-screen-2xl t-mx-auto t-px-4 lg:t-px-8">
                {/* Logo and Social Media Section - CarMax Style */}
                <div className="t-flex t-flex-col lg:t-flex-row t-items-start lg:t-items-center t-justify-between t-mb-8 lg:t-mb-12">
                    <Link
                        href={props?.headerData?.["link5"]?.link || "/"}
                        className="t-inline-block t-transition-opacity t-duration-200 hover:t-opacity-80"
                        aria-label="THIScar - Return to homepage">
                        <SanityImage
                            src={props?.headerData?.["link5"]?.image}
                            defaultImage={GetDefaultImagePath(LogoPath)}
                            alt="THIScar - Buy and Sell Quality Used Cars Online"
                            className="t-w-auto t-h-auto"
                            width={137}
                            height={51}
                            priority={false}
                            loading="lazy"
                        />
                    </Link>

                    {/* Social Media - Next to Logo */}
                    {(hasFacebookLink || hasInstagramLink) && <div className="t-flex t-items-center t-space-x-4 t-mt-4 lg:t-mt-0">
                        <span className="t-text-white t-text-sm t-font-medium t-mr-2">Follow Us:</span>
                        {hasFacebookLink && (
                            <Link
                                href={facebookLink}
                                className="t-text-white hover:t-text-brand-yellow t-transition-colors t-duration-200"
                                aria-label="Follow THIScar on Facebook">
                                <svg
                                    className="t-w-6 t-h-6"
                                    fill="currentColor"
                                    viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </Link>
                        )}
                        {hasInstagramLink && (
                            <Link
                                href={instagramLink}
                                className="t-text-white hover:t-text-brand-yellow t-transition-colors t-duration-200"
                                aria-label="Follow THIScar on Instagram">
                                <svg
                                    className="t-w-6 t-h-6"
                                    fill="currentColor"
                                    viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                            </Link>
                        )}
                    </div>}
                </div>

                {!isReferralPath && (
                    <div className="t-grid t-grid-cols-1 lg:t-grid-cols-4 t-gap-8 lg:t-gap-12 t-mb-8 lg:t-mb-12">
                        {/* Navigation Links */}
                        <nav aria-label="Footer navigation">
                            <h3 className="t-text-lg t-font-semibold t-text-brand-yellow t-mb-4">Navigation</h3>
                            <ul className="t-space-y-3">
                                {props.headerData?.["link1"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.headerData["link1"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.headerData["link1"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.headerData?.["link2"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.headerData["link2"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.headerData["link2"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.headerData?.["link3"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.headerData["link3"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.headerData["link3"].linkText}
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    {/* Was a bare link, so footer financing clicks
                                        recorded no intent at all and arrived in the
                                        CRM with no attribution. */}
                                    <RouteOneFinancingButton
                                        pageSection="footer"
                                        ariaLabel="Start your financing application"
                                        className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                        Financing
                                    </RouteOneFinancingButton>
                                </li>
                                {props.headerData?.["link4"]?.["link4"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.headerData["link4"]["link4"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.headerData["link4"]["link4"].linkText}
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </nav>

                        {/* Company Links */}
                        <nav aria-label="Company links">
                            <h3 className="t-text-lg t-font-semibold t-text-brand-yellow t-mb-4">Company</h3>
                            <ul className="t-space-y-3">
                                <li><Link href="/blog" className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">The THIScar Journal</Link></li>
                                {props.footerData?.["link1"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.footerData["link1"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.footerData["link1"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.footerData?.["link2"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.footerData["link2"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.footerData["link2"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.footerData?.["link3"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.footerData["link3"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.footerData["link3"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.footerData?.["link4"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.footerData["link4"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.footerData["link4"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.footerData?.["link5"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.footerData["link5"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.footerData["link5"].linkText}
                                        </Link>
                                    </li>
                                )}
                                {props.footerData?.["link6"]?.linkText?.trim() && (
                                    <li>
                                        <Link
                                            href={CheckLink(props.footerData["link6"].link)}
                                            className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                            {props.footerData["link6"].linkText}
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </nav>

                        {/* Car Makes Section - All Makes in 3 Columns for SEO */}
                        <nav
                            className="lg:t-col-span-2"
                            aria-label="Shop by car make">
                            <h3 className="t-text-lg t-font-semibold t-text-brand-yellow t-mb-4">Shop by Make</h3>
                            <ul className="t-grid t-grid-cols-3 t-gap-x-4 t-gap-y-2">
                                {props.facets?.make
                                    ?.filter((x) => x.value !== "Fisker")
                                    ?.map((facet) => (
                                        <li key={facet.value}>
                                            <Link
                                                href={`/cars?make=${facet.value}`}
                                                className="t-text-gray-300 hover:t-text-brand-yellow t-transition-colors t-duration-200 t-text-sm t-block t-py-1"
                                                prefetch={false}>
                                                {facet.value}
                                            </Link>
                                        </li>
                                    ))}
                            </ul>
                        </nav>
                    </div>
                )}

                {/* Copyright Section */}
                <div
                    className={`t-pt-8 t-border-t t-border-gray-700 ${!isReferralPath ? "t-text-center lg:t-text-left" : "t-text-right"}`}>
                    <div className="t-text-gray-400 t-text-sm">
                        &copy; {currentYear} {props.footerData?.["copyRightsText1"] || "THIScar"}&trade;{" "}
                        {props.footerData?.["copyRightsText2"]}
                        {props.footerData?.["termsConditions"]?.linkText?.trim() && (
                            <>
                                {" | "}
                                <Link
                                    href={CheckLink(props.footerData["termsConditions"].link)}
                                    className="t-text-gray-400 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                                    {props.footerData["termsConditions"].linkText}
                                </Link>
                            </>
                        )}
                        {" | "}
                        <Link
                            href="/sms-terms"
                            className="t-text-gray-400 hover:t-text-brand-yellow t-transition-colors t-duration-200">
                            SMS Terms
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
